import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, CheckCircle2, Plus, Search, SearchX, Truck } from 'lucide-react';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Dialog } from '@/components/ui/Dialog';
import { showToast } from '@/components/ui/Toast';

interface EquipmentItem {
  unitNumber: string;
  status?: string;
}

type EquipTab = 'power' | 'trailer';

export function EquipmentPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<EquipTab>('power');
  const [query, setQuery] = useState('');

  // The FileMaker layout does not yet expose assigned equipment, so the lists
  // start empty (mirrors the native TODO). Drivers can still add missing units.
  const [powerUnits, setPowerUnits] = useState<EquipmentItem[]>([]);
  const [trailers, setTrailers] = useState<EquipmentItem[]>([]);

  const [selectedPower, setSelectedPower] = useState<string | null>(null);
  const [selectedTrailer, setSelectedTrailer] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addUnit, setAddUnit] = useState('');
  const [addType, setAddType] = useState<EquipTab>('power');

  const isPower = tab === 'power';
  const source = isPower ? powerUnits : trailers;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return source.filter((e) => e.unitNumber.toLowerCase().includes(q));
  }, [source, query]);

  const switchTab = (t: EquipTab) => {
    setTab(t);
    setQuery('');
  };

  const toggleSelect = (unit: string) => {
    if (isPower) {
      setSelectedPower((cur) => (cur === unit ? null : unit));
    } else {
      setSelectedTrailer((cur) => (cur === unit ? null : unit));
    }
  };

  const confirmHookup = () => {
    showToast('Equipment hookup saved.');
    navigate(-1);
  };

  const openAdd = () => {
    setAddUnit('');
    setAddType(tab);
    setAddOpen(true);
  };

  const submitAdd = () => {
    const unit = addUnit.trim();
    if (!unit) return;
    const item: EquipmentItem = { unitNumber: unit, status: 'Added by driver' };
    if (addType === 'power') setPowerUnits((cur) => [item, ...cur]);
    else setTrailers((cur) => [item, ...cur]);
    setAddOpen(false);
    showToast('Equipment added.');
  };

  const selected = isPower ? selectedPower : selectedTrailer;
  const canConfirm = selectedPower !== null || selectedTrailer !== null;

  return (
    <AppShell
      title="Equipment"
      showBack
      rightSlot={
        <Button variant="ghost" size="sm" aria-label="Add missing equipment" onClick={openAdd}>
          <Plus className="h-5 w-5" />
        </Button>
      }
    >
      <div className="mb-3 grid grid-cols-2 overflow-hidden rounded border border-brand-border">
        <button
          type="button"
          className={`py-2 text-sm font-semibold ${
            isPower ? 'bg-brand-primaryBlue text-white' : 'bg-brand-surface text-brand-muted'
          }`}
          onClick={() => switchTab('power')}
        >
          Power Units
        </button>
        <button
          type="button"
          className={`py-2 text-sm font-semibold ${
            !isPower ? 'bg-brand-primaryBlue text-white' : 'bg-brand-surface text-brand-muted'
          }`}
          onClick={() => switchTab('trailer')}
        >
          Trailers
        </button>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-muted" />
        <Input
          aria-label="Search equipment"
          placeholder="Search equipment..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {canConfirm && (
        <div className="mt-3 flex items-center gap-2 rounded border border-brand-primaryBlue/30 bg-brand-primaryBlue/10 px-3 py-2 text-sm">
          <CheckCircle2 className="h-5 w-5 text-brand-primaryBlue" />
          {selectedPower && <span>Power: {selectedPower}</span>}
          {selectedPower && selectedTrailer && <span className="text-brand-muted">|</span>}
          {selectedTrailer && <span>Trailer: {selectedTrailer}</span>}
        </div>
      )}

      <div className="mt-3">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-brand-muted">
            <SearchX className="h-12 w-12" />
            <p>No equipment found</p>
            <Button variant="ghost" size="sm" onClick={openAdd}>
              <Plus className="h-4 w-4" />
              Add Missing Equipment
            </Button>
          </div>
        ) : (
          <ul className="space-y-2">
            {filtered.map((item) => {
              const isSel = selected === item.unitNumber;
              return (
                <li key={item.unitNumber}>
                  <button
                    type="button"
                    onClick={() => toggleSelect(item.unitNumber)}
                    className={`flex w-full items-center gap-3 rounded border px-3 py-3 text-left ${
                      isSel
                        ? 'border-brand-primaryBlue bg-brand-primaryBlue/15'
                        : 'border-brand-border bg-brand-surface'
                    }`}
                  >
                    {isPower ? (
                      <Car className={`h-5 w-5 ${isSel ? 'text-brand-primaryBlue' : 'text-brand-muted'}`} />
                    ) : (
                      <Truck className={`h-5 w-5 ${isSel ? 'text-brand-primaryBlue' : 'text-brand-muted'}`} />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className={`truncate ${isSel ? 'font-bold' : ''}`}>{item.unitNumber}</div>
                      {item.status && (
                        <div className="truncate text-xs text-brand-muted">{item.status}</div>
                      )}
                    </div>
                    {isSel && <CheckCircle2 className="h-5 w-5 text-brand-primaryBlue" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="pb-safe sticky bottom-0 mt-4 pt-2">
        <Button variant="primary" size="lg" fullWidth disabled={!canConfirm} onClick={confirmHookup}>
          Confirm Hookup
        </Button>
      </div>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} title="Add Missing Equipment">
        <div className="space-y-3">
          <Input
            label="Unit Number"
            value={addUnit}
            onChange={(e) => setAddUnit(e.target.value)}
            autoCapitalize="characters"
          />
          <div className="space-y-1">
            <label htmlFor="equip-type" className="label">
              Type
            </label>
            <select
              id="equip-type"
              className="input"
              value={addType}
              onChange={(e) => setAddType(e.target.value as EquipTab)}
            >
              <option value="power">Power Unit</option>
              <option value="trailer">Trailer</option>
            </select>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setAddOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!addUnit.trim()} onClick={submitAdd}>
            Add
          </Button>
        </div>
      </Dialog>
    </AppShell>
  );
}
